import { ConnectionCheck } from 'livekit-client';
import type { CheckInfo } from 'livekit-client';
import { ElectronAPI } from '#~/electron/index.js';
import { logger } from '#~/logger/index.js';

export type LiveKitConnectionDiagnosticCheck = 'websocket' | 'webrtc' | 'turn';
export type LiveKitConnectionDiagnosticMode = 'cloud' | 'local';
// ENG-3868 §3 / ENG-3942 — fired post-Connected, cloud-only, once per app
// launch. The original synchronous-constructor + module-flag version
// contended with the local-bridge socket; see ENG-3942.
export type LiveKitConnectionDiagnosticTrigger =
  | 'cloud-connect-failed'
  | 'local-connect-failed'
  | 'manual'
  | 'startup';

export type LiveKitConnectionDiagnosticResult = {
  check: LiveKitConnectionDiagnosticCheck;
  status: 'idle' | 'running' | 'skipped' | 'success' | 'failed';
  elapsedMs: number;
  errorCount: number;
  warningCount: number;
};

export type LiveKitConnectionDiagnosticSummary = {
  runId: string;
  status: 'skipped' | 'success' | 'failed';
  results: LiveKitConnectionDiagnosticResult[];
};

export type LiveKitConnectionDiagnosticOptions = {
  url: string;
  token: string;
  mode: LiveKitConnectionDiagnosticMode;
  trigger: LiveKitConnectionDiagnosticTrigger;
  roomId?: string;
  agentId?: string;
  cause?: unknown;
  checks?: readonly LiveKitConnectionDiagnosticCheck[];
  throttleMs?: number;
};

type DiagnosticEvent = {
  level: 'info' | 'warning' | 'error';
  stage: string;
  runId: string;
  mode: LiveKitConnectionDiagnosticMode;
  trigger: LiveKitConnectionDiagnosticTrigger;
  roomId?: string;
  agentId?: string;
  check?: LiveKitConnectionDiagnosticCheck;
  status?: string;
  elapsedMs?: number;
  message?: string;
};

const DEFAULT_CHECKS: readonly LiveKitConnectionDiagnosticCheck[] = ['websocket', 'webrtc', 'turn'];
const DEFAULT_THROTTLE_MS = 60_000;
const recentRuns = new Map<string, number>();

// LiveKit's d.ts exports CheckStatus as type-only; values verified from SDK source.
const LIVEKIT_CHECK_STATUS = {
  IDLE: 0,
  RUNNING: 1,
  SKIPPED: 2,
  SUCCESS: 3,
  FAILED: 4
} as const;

export async function runLiveKitConnectionDiagnostics(
  opts: LiveKitConnectionDiagnosticOptions
): Promise<LiveKitConnectionDiagnosticSummary> {
  const runId = newRunId();
  const checks = opts.checks ?? DEFAULT_CHECKS;
  const base = eventBase(opts, runId);
  const throttleKey = `${opts.mode}:${opts.trigger}:${opts.url}`;

  if (shouldThrottle(throttleKey, opts.throttleMs ?? DEFAULT_THROTTLE_MS)) {
    await emitDiagnosticEvent({
      ...base,
      level: 'info',
      stage: 'skipped',
      status: 'skipped',
      message: 'recent run already captured for this LiveKit endpoint'
    });
    return { runId, status: 'skipped', results: [] };
  }

  const missing = missingBrowserWebRTCPrimitives();
  if (missing.length > 0) {
    await emitDiagnosticEvent({
      ...base,
      level: 'warning',
      stage: 'skipped',
      status: 'skipped',
      message: `missing browser WebRTC primitives: ${missing.join(',')}`
    });
    return { runId, status: 'skipped', results: [] };
  }

  const startEvent: DiagnosticEvent = {
    ...base,
    level: 'info',
    stage: 'start'
  };
  if (opts.cause) {
    startEvent.message = errorToMessage(opts.cause, opts);
  }
  await emitDiagnosticEvent(startEvent);

  // ENG-3868 §3 / ENG-3942 / spec §6.5 — every ConnectionCheck MUST be
  // disposed (success, failure, OR skip) so the underlying WebRTC + WS
  // handles do not accumulate across reload-driven re-fires. livekit-client
  // v2.15.2 ConnectionCheck exposes no public dispose()/close(); verified
  // against node_modules/livekit-client/dist/src/connectionHelper/
  // ConnectionCheck.d.ts. The individual Checker subclasses (WebSocketCheck,
  // WebRTCCheck, TURNCheck) already close their Room internally via
  // Checker.run() -> disconnect() (Checker.ts:87) before our await resolves,
  // so the RTCPeerConnection is closed. The remaining live resource on the
  // outer ConnectionCheck is its EventEmitter listener table — drop the
  // reference + removeAllListeners() covers it. SDK follow-up: ask
  // livekit-client to add a public dispose() to ConnectionCheck so callers
  // can stop relying on this internal contract (see ENG-3942 wiki).
  const checker = new ConnectionCheck(opts.url, opts.token, {
    roomOptions: { adaptiveStream: false },
    connectOptions: { autoSubscribe: false }
  });
  const results: LiveKitConnectionDiagnosticResult[] = [];

  try {
    for (const check of checks) {
      const result = await runOneCheck(checker, check, base);
      results.push(result);
    }

    const status = results.some((result) => result.status === 'failed') ? 'failed' : 'success';
    await emitDiagnosticEvent({
      ...base,
      level: status === 'failed' ? 'warning' : 'info',
      stage: 'complete',
      status,
      message: `checks=${results.length} failed=${results.filter((r) => r.status === 'failed').length}`
    });

    return { runId, status, results };
  } finally {
    disposeChecker(checker);
  }
}

function disposeChecker(checker: ConnectionCheck): void {
  // Best-effort teardown — diagnostics must never affect the LiveKit
  // connection path (C7 / spec §11 / non-goal §4).
  try {
    // ConnectionCheck extends EventEmitter; removeAllListeners drops the
    // 'checkUpdate' subscriptions populated by createAndRunCheck.
    checker.removeAllListeners?.();
  } catch {
    // swallow — disposal failures must not propagate.
  }
}

export function resetLiveKitConnectionDiagnosticsForTests(): void {
  recentRuns.clear();
}

async function runOneCheck(
  checker: ConnectionCheck,
  check: LiveKitConnectionDiagnosticCheck,
  base: Omit<DiagnosticEvent, 'level' | 'stage'>
): Promise<LiveKitConnectionDiagnosticResult> {
  const startedAt = Date.now();
  await emitDiagnosticEvent({
    ...base,
    level: 'info',
    stage: 'check_start',
    check
  });

  try {
    const info = await runCheck(checker, check);
    const elapsedMs = Date.now() - startedAt;
    const status = statusLabel(info.status);
    const errorCount = info.logs.filter((entry) => entry.level === 'error').length;
    const warningCount = info.logs.filter((entry) => entry.level === 'warning').length;

    await emitDiagnosticEvent({
      ...base,
      level: status === 'failed' ? 'warning' : 'info',
      stage: 'check_complete',
      check,
      status,
      elapsedMs,
      message: `warnings=${warningCount} errors=${errorCount}`
    });

    for (const entry of info.logs.slice(-20)) {
      await emitDiagnosticEvent({
        ...base,
        level: entry.level,
        stage: 'check_log',
        check,
        status,
        message: entry.message
      });
    }

    return { check, status, elapsedMs, errorCount, warningCount };
  } catch (error) {
    const elapsedMs = Date.now() - startedAt;
    await emitDiagnosticEvent({
      ...base,
      level: 'error',
      stage: 'check_error',
      check,
      status: 'failed',
      elapsedMs,
      message: errorToMessage(error)
    });
    return {
      check,
      status: 'failed',
      elapsedMs,
      errorCount: 1,
      warningCount: 0
    };
  }
}

function runCheck(
  checker: ConnectionCheck,
  check: LiveKitConnectionDiagnosticCheck
): Promise<CheckInfo> {
  switch (check) {
    case 'websocket':
      return checker.checkWebsocket();
    case 'webrtc':
      return checker.checkWebRTC();
    case 'turn':
      return checker.checkTURN();
    default:
      throw new Error(`unsupported LiveKit diagnostic check: ${check}`);
  }
}

async function emitDiagnosticEvent(event: DiagnosticEvent): Promise<void> {
  const safeEvent = event.message
    ? { ...event, message: redactDiagnosticMessage(event.message) }
    : event;
  const line = formatDiagnosticLine(safeEvent);
  const logFn =
    safeEvent.level === 'error'
      ? logger.error
      : safeEvent.level === 'warning'
        ? logger.warning
        : logger.info;
  const args: [string] | [string, { message: string }] = safeEvent.message
    ? [line, { message: safeEvent.message }]
    : [line];

  // Renderer logs remain useful in browsers; Electron additionally persists them via loggy.
  logFn.call(logger, ...args);

  try {
    await ElectronAPI.logLiveKitDiagnostic(safeEvent);
  } catch {
    // Diagnostics are best-effort and must never affect the LiveKit path.
  }
}

function eventBase(
  opts: LiveKitConnectionDiagnosticOptions,
  runId: string
): Omit<DiagnosticEvent, 'level' | 'stage'> {
  const base: Omit<DiagnosticEvent, 'level' | 'stage'> = {
    runId,
    mode: opts.mode,
    trigger: opts.trigger
  };

  if (opts.roomId) base.roomId = opts.roomId;
  if (opts.agentId) base.agentId = opts.agentId;
  return base;
}

function shouldThrottle(key: string, throttleMs: number): boolean {
  const now = Date.now();
  const last = recentRuns.get(key);
  if (last && now - last < throttleMs) {
    return true;
  }
  recentRuns.set(key, now);
  return false;
}

function missingBrowserWebRTCPrimitives(): string[] {
  const missing: string[] = [];
  if (typeof window === 'undefined') missing.push('window');
  if (typeof globalThis.RTCPeerConnection === 'undefined') missing.push('RTCPeerConnection');
  if (typeof globalThis.RTCIceCandidate === 'undefined') missing.push('RTCIceCandidate');
  return missing;
}

function statusLabel(status: CheckInfo['status']): LiveKitConnectionDiagnosticResult['status'] {
  switch (status) {
    case LIVEKIT_CHECK_STATUS.IDLE:
      return 'idle';
    case LIVEKIT_CHECK_STATUS.RUNNING:
      return 'running';
    case LIVEKIT_CHECK_STATUS.SKIPPED:
      return 'skipped';
    case LIVEKIT_CHECK_STATUS.SUCCESS:
      return 'success';
    case LIVEKIT_CHECK_STATUS.FAILED:
      return 'failed';
    default:
      return 'failed';
  }
}

function formatDiagnosticLine(event: DiagnosticEvent): string {
  const fields = [
    `run_id=${event.runId}`,
    `mode=${event.mode}`,
    `trigger=${event.trigger}`,
    event.roomId ? `room_id=${event.roomId}` : undefined,
    event.agentId ? `agent_id=${event.agentId}` : undefined,
    event.check ? `check=${event.check}` : undefined,
    event.status ? `status=${event.status}` : undefined,
    typeof event.elapsedMs === 'number' ? `elapsed_ms=${event.elapsedMs}` : undefined
  ].filter(Boolean);

  return `livekit.renderer_diagnostic stage=${event.stage} ${fields.join(' ')}`;
}

function newRunId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `lkdiag-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function errorToMessage(
  error: unknown,
  sensitive?: Pick<LiveKitConnectionDiagnosticOptions, 'token' | 'url'>
): string {
  const raw = error instanceof Error ? error.message : String(error);
  return redactDiagnosticMessage(raw, sensitive);
}

function redactDiagnosticMessage(
  message: string,
  sensitive?: Pick<LiveKitConnectionDiagnosticOptions, 'token' | 'url'>
): string {
  // Keep failure context useful without persisting LiveKit credentials.
  let cleaned = message;
  if (sensitive?.token) {
    cleaned = cleaned.split(sensitive.token).join('[LIVEKIT_TOKEN_REDACTED]');
  }
  if (sensitive?.url) {
    cleaned = cleaned.split(sensitive.url).join('[LIVEKIT_URL_REDACTED]');
  }
  return cleaned
    .replace(
      /token=([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/gi,
      'token=[LIVEKIT_TOKEN_REDACTED]'
    )
    .replace(
      /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
      '[LIVEKIT_TOKEN_REDACTED]'
    );
}
